/**
 * Read-only "Test Cases" tab for an automation record.
 *
 * Renders the agent-authored fixtures (see ../utils/fixtures.ts for the
 * stored shape) as one card each: the plain-language label as the heading,
 * the trigger or scheduled instant it seeds, and the events the script is
 * expected to emit. Nothing here comes from the backend as markup — it is all
 * laid out from structured data. The fixture slug (`name`) is deliberately
 * not shown: it only identifies the fixture to the validator, and nothing on
 * this surface refers to it.
 *
 * Fixture-local aliases (`contact-quiet`, `new-note`, `user-you`) are
 * resolved against the fixture's own `entities` / `references` so an expected
 * event reads "Priya Shah (Contact)" rather than the alias the script author
 * chose, and property names resolve to their schema labels ("Date", not
 * `ahoy_date`) through the same property-type schema the overview uses.
 * Anything unresolvable falls back to the raw value.
 *
 * A pending proposal's fixtures are projected onto the record like any other
 * proposed property, so the same read (`getEffectiveFixtures`) makes test
 * cases reviewable before approval, exactly like the Code tab.
 */

import { useQueries } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { useMemo } from 'react';

import { useDisplayTimezone } from '@/hooks/useDisplayTimezone';
import { Badge } from '@/lib/base/badges/badges';
import {
  fetchPropertySchemas,
  propertySchemaQueryKey,
} from '@/lib/crm/hooks/usePropertySchema';
import { formatDateTimeCompact } from '@/lib/crm/object-cards/formatDateTime';
import { getEntityTypeLabel } from '@/lib/crm/utils/entityTypeUtils';
import { getDisplayNameFromProperties } from '@/lib/crm/utils/getDisplayName';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import type { EventTypeName } from '../timeline/types';
import { EVENT_TYPE_LABELS } from '../timeline/types';
import type {
  Fixture,
  FixtureEntity,
  FixtureExpectEvent,
} from '../utils/fixtures';
import { getEffectiveFixtures } from '../utils/fixtures';

const EXPECT_EVENT_TYPE_LABELS: Record<string, string> = {
  object_created: 'Object created',
  property_updated: 'Property updated',
  association_added: 'Association added',
  association_removed: 'Association removed',
  object_deleted: 'Object deleted',
  web_form_submitted: 'Web form submitted',
  web_page_viewed: 'Web page viewed',
};

// Schemas change rarely; matches usePropertySchema's own stale window.
const PROPERTY_SCHEMA_STALE_TIME_MS = 5 * 60 * 1000;

/** entity type → property name → label, for every type the fixtures touch. */
type PropertyLabels = Record<string, Record<string, string>>;

const NO_PROPERTY_LABELS: PropertyLabels = {};

function eventTypeLabel(name: string): string {
  return EVENT_TYPE_LABELS[name as EventTypeName] ?? name;
}

function firstString(value: unknown): string | null {
  const candidate = Array.isArray(value) ? value[0] : value;
  const unwrapped = getPropertyValue<unknown>(
    candidate as Parameters<typeof getPropertyValue>[0]
  );
  return typeof unwrapped === 'string' && unwrapped.trim()
    ? unwrapped.trim()
    : null;
}

/**
 * A human name for a seeded fixture entity, from whatever the author gave it:
 * the CRM display-name fallbacks, then person names, then a title, then an
 * email. Null when the fixture seeded only ids.
 */
function fixtureEntityName(entity: FixtureEntity): string | null {
  const properties = entity.properties ?? {};
  const displayName = getDisplayNameFromProperties(
    properties,
    null,
    entity.entity_type_name
  );
  if (displayName) {
    return displayName;
  }
  const personName = [
    firstString(properties.ahoy_first_name),
    firstString(properties.ahoy_last_name),
  ]
    .filter(Boolean)
    .join(' ');
  if (personName) {
    return personName;
  }
  return (
    firstString(properties.ahoy_title) ?? firstString(properties.ahoy_email)
  );
}

interface AliasIndex {
  /** alias → "Name (Type)" / "Type" / "New type" */
  labels: Map<string, string>;
  /** alias → entity type, so a property-only event can find its schema */
  types: Map<string, string>;
}

/**
 * Everything a fixture names, by alias: seeded entities read as "Name (Type)"
 * (or just "Type" when nameless), records the script is expected to create
 * read as "New type". Each alias also remembers its entity type.
 */
function buildAliasIndex(fixture: Fixture): AliasIndex {
  const labels = new Map<string, string>();
  const types = new Map<string, string>();
  for (const entity of fixture.entities ?? []) {
    if (!entity.entity_id) {
      continue;
    }
    const typeLabel = getEntityTypeLabel(entity.entity_type_name);
    const name = fixtureEntityName(entity);
    labels.set(entity.entity_id, name ? `${name} (${typeLabel})` : typeLabel);
    types.set(entity.entity_id, entity.entity_type_name);
  }
  for (const reference of fixture.references ?? []) {
    labels.set(
      reference.name,
      `New ${getEntityTypeLabel(reference.entity_type_name).toLowerCase()}`
    );
    types.set(reference.name, reference.entity_type_name);
  }
  return { labels, types };
}

/** The entity type an expected event's properties belong to, if knowable. */
function eventEntityType(
  event: FixtureExpectEvent,
  aliases: AliasIndex
): string | undefined {
  return (
    event.entity_type_name ??
    (event.entity_id ? aliases.types.get(event.entity_id) : undefined)
  );
}

/** Every entity type whose property labels the fixtures will need. */
function collectEntityTypes(fixtures: Fixture[]): string[] {
  const types = new Set<string>();
  for (const fixture of fixtures) {
    const aliases = buildAliasIndex(fixture);
    for (const event of fixture.expect?.events ?? []) {
      if (event.properties && Object.keys(event.properties).length > 0) {
        const type = eventEntityType(event, aliases);
        if (type) {
          types.add(type);
        }
      }
    }
  }
  return [...types].sort();
}

function buildEventDetail(
  event: FixtureExpectEvent,
  aliases: AliasIndex,
  propertyLabels: PropertyLabels
): string {
  const resolve = (alias: string): string => aliases.labels.get(alias) ?? alias;
  const parts: string[] = [];
  if (event.entity_id) {
    parts.push(resolve(event.entity_id));
  }
  if (
    (event.type === 'association_added' ||
      event.type === 'association_removed') &&
    event.target_entity_id
  ) {
    parts.push(`→ ${resolve(event.target_entity_id)}`);
  } else if (
    (event.type === 'object_created' || event.type === 'property_updated') &&
    event.properties &&
    Object.keys(event.properties).length > 0
  ) {
    const type = eventEntityType(event, aliases);
    const labels = type ? propertyLabels[type] : undefined;
    const names = Object.keys(event.properties).map(
      name => labels?.[name] ?? name
    );
    parts.push(`sets ${names.join(', ')}`);
  }
  return parts.join(' ');
}

function scheduledAtLabel(isoString: string, timezone: string): string {
  const { date, time } = formatDateTimeCompact(isoString, timezone);
  return time ? `${date}, ${time}` : date;
}

/**
 * Property labels for every entity type the fixtures reference — one
 * property-schema query per type, sharing usePropertySchema's cache key so
 * the overview's schema fetch and this one never disagree. Resolves to the
 * raw property name for anything not (yet) in the schema.
 */
function useFixturePropertyLabels(
  orgId: number,
  entityTypes: string[]
): PropertyLabels {
  return useQueries({
    queries: entityTypes.map(entityType => ({
      queryKey: propertySchemaQueryKey(orgId, entityType),
      queryFn: () => fetchPropertySchemas(orgId, entityType),
      enabled: orgId > 0,
      staleTime: PROPERTY_SCHEMA_STALE_TIME_MS,
    })),
    combine: results => {
      if (results.every(r => r.data === undefined)) {
        return NO_PROPERTY_LABELS;
      }
      const labels: PropertyLabels = {};
      results.forEach((result, index) => {
        const byName: Record<string, string> = {};
        for (const property of result.data ?? []) {
          byName[property.property_type_name] = property.label;
        }
        labels[entityTypes[index]] = byName;
      });
      return labels;
    },
  });
}

function FixtureCard({
  fixture,
  timezone,
  propertyLabels,
}: {
  fixture: Fixture;
  timezone: string;
  propertyLabels: PropertyLabels;
}): ReactElement {
  const events = fixture.expect?.events ?? [];
  const aliases = useMemo(() => buildAliasIndex(fixture), [fixture]);
  // The label is the sentence the author wrote for a reviewer. The slug only
  // exists for the validator, so it stays out of the UI; a fixture with no
  // label falls back to it rather than rendering an empty heading.
  const heading = fixture.label?.trim() || fixture.name;
  return (
    <div className='rounded-lg border border-border-secondary p-3'>
      <div className='text-body font-medium text-fg-primary'>{heading}</div>
      {fixture.trigger_event != null && (
        <div className='mt-1.5 text-body-small text-fg-secondary'>
          On {eventTypeLabel(fixture.trigger_event.event_type_name)} &middot;{' '}
          {getEntityTypeLabel(fixture.trigger_event.entity_type_name)}
        </div>
      )}
      {fixture.trigger_event == null && fixture.scheduled_at != null && (
        <div className='mt-1.5 text-body-small text-fg-secondary'>
          Scheduled &middot; {scheduledAtLabel(fixture.scheduled_at, timezone)}
        </div>
      )}
      <div className='mt-2'>
        <div className='text-caption text-fg-quaternary mb-1'>
          Expected events
        </div>
        {events.length === 0 ? (
          <div className='text-body-small text-fg-tertiary'>
            No expected events
          </div>
        ) : (
          <div className='flex flex-col gap-1'>
            {events.map(event => {
              const detail = buildEventDetail(event, aliases, propertyLabels);
              const eventKey = `${event.type}:${event.entity_id ?? ''}:${event.target_entity_id ?? ''}:${event.association_type_name ?? ''}`;
              return (
                <div
                  key={eventKey}
                  className='flex flex-wrap items-center gap-1.5'
                >
                  <Badge type='pill-color' color='gray' size='sm'>
                    {EXPECT_EVENT_TYPE_LABELS[event.type] ?? event.type}
                  </Badge>
                  {detail && (
                    <span className='text-body-small text-fg-secondary'>
                      {detail}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export function AutomationTestCasesSection({
  object,
}: {
  object: CrmObject;
}): ReactElement | null {
  const timezone = useDisplayTimezone();
  const fixtures = useMemo(() => getEffectiveFixtures(object), [object]);
  const entityTypes = useMemo(() => collectEntityTypes(fixtures), [fixtures]);
  const propertyLabels = useFixturePropertyLabels(object.org_id, entityTypes);
  if (fixtures.length === 0) {
    return null;
  }
  return (
    <div className='space-y-3'>
      {/* Most people reading this tab aren't engineers: say what a test case
          is and what it's for before showing any. */}
      <p className='text-body-small text-fg-secondary'>
        Test cases are example situations we check this automation against. Each
        one sets up a few records, runs the automation on them, and lists the
        changes it should make. We run them whenever this automation is created
        or edited, so a change can&apos;t quietly break it.
      </p>
      {fixtures.map(fixture => (
        <FixtureCard
          key={fixture.name}
          fixture={fixture}
          timezone={timezone}
          propertyLabels={propertyLabels}
        />
      ))}
    </div>
  );
}
